import { createFileRoute, Link } from "@tanstack/react-router";
import { lastmodFor } from "@/lib/route-lastmod";
import { faqPageJson } from "@/lib/faq-schema";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { PageSection } from "@/components/site/PageSection";

const URL =
  "https://virginiabariatric.com/resources/managing-weight-regain-after-bariatric-surgery";
const TITLE = "Weight Regain After Bariatric Surgery | Virginia Bariatric";
const DESCRIPTION =
  "Why weight comes back after a gastric sleeve or bypass, and what actually helps: GLP-1 medications, revision surgery, and long-term metabolic follow-up in Virginia.";
const OG_IMAGE =
  "https://virginiabariatric.com/__l5e/assets-v1/64a2217d-5454-444b-b82e-298e33a87aa9/og-hero.jpg";
const OG_IMAGE_ALT =
  "Virginia Bariatric Surgery — Dr. Matthew Fitzer's Northern Virginia weight-loss practice";

const FAQS: { q: string; a: string }[] = [
  {
    q: "How much weight regain after gastric sleeve is normal?",
    a: "Most patients regain some weight after reaching their lowest point, and a rebound of roughly 5 to 10 percent of body weight between years two and five is common and expected. Regain that erases more than about a quarter of the weight you originally lost, or that brings back conditions such as diabetes, sleep apnea, or reflux, is worth evaluating rather than waiting out.",
  },
  {
    q: "Why do I regain weight even though I had surgery?",
    a: "Weight regain is biological, not a character flaw. After major weight loss the body lowers its resting metabolic rate, raises hunger hormones such as ghrelin, and lowers fullness signals such as GLP-1 and PYY. The stomach also stretches somewhat over time. Those forces push weight back up unless they are actively counteracted.",
  },
  {
    q: "Can GLP-1 medications be used after bariatric surgery?",
    a: "Yes. Medications such as semaglutide and tirzepatide are frequently used after sleeve gastrectomy or gastric bypass to treat regain, and they directly replace the fullness signaling that fades over time. For many patients this is the least invasive effective option and avoids a second operation.",
  },
  {
    q: "What are my bariatric revision options?",
    a: "The most common revisions are converting a sleeve gastrectomy to a Roux-en-Y gastric bypass, converting a sleeve to a duodenal switch or SADI, revising a prior bypass, or removing a failed adjustable band and converting to a sleeve or bypass. Which one fits depends on your anatomy, your reflux history, and what your imaging and endoscopy show.",
  },
  {
    q: "Is revision surgery riskier than the first operation?",
    a: "Revisional surgery is technically more demanding than a first-time operation because of scar tissue and altered anatomy, so it should be done by a surgeon who performs it regularly. Dr. Fitzer has performed more than 6,000 operations, including a large volume of revisions, and does them laparoscopically in the great majority of cases.",
  },
  {
    q: "When should I call about weight regain?",
    a: "Call as soon as the trend is clear rather than after several years. Regain is far easier to reverse at 15 pounds than at 60, and early evaluation often means medication and follow-up rather than a second operation.",
  },
];

export const Route = createFileRoute(
  "/resources/managing-weight-regain-after-bariatric-surgery",
)({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "og:url", content: URL },
        { property: "og:image", content: OG_IMAGE },
        { property: "og:image:alt", content: OG_IMAGE_ALT },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: OG_IMAGE },
        { name: "twitter:image:alt", content: OG_IMAGE_ALT },
      ] : []),
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "es", href: "https://virginiabariatric.com/es/recuperacion-de-peso-despues-de-cirugia-bariatrica" },
      { rel: "alternate", hrefLang: "x-default", href: URL },
    ],
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          dateModified: lastmodFor("/resources/managing-weight-regain-after-bariatric-surgery"),
          workTranslation: { "@id": "https://virginiabariatric.com/es/recuperacion-de-peso-despues-de-cirugia-bariatrica#webpage" },
          lastReviewed: "2026-08-09",
          "@id": `${URL}#webpage`,
          url: URL,
          name: "Managing Weight Regain After Bariatric Surgery",
          description: DESCRIPTION,
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          about: [
            {
              "@type": "MedicalCondition",
              name: "Obesity",
              code: { "@type": "MedicalCode", code: "E66", codingSystem: "ICD-10" },
            },
          ],
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Patients with weight regain after bariatric surgery",
          },
          specialty: "https://schema.org/Surgical",
          reviewedBy: PHYSICIAN_REF,
          mainEntity: { "@id": `${URL}#faq` },
        }),
      },
      {
        type: "application/ld+json",
        children: faqPageJson({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          url: URL,
          isPartOf: { "@id": `${URL}#webpage` },
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ] : [],
  }),
  component: WeightRegainPage,
});

function H2({ children, id }: { children: React.ReactNode; id?: string }) {
  return (
    <h2
      id={id}
      className="text-2xl md:text-3xl font-serif text-brand-mid text-center pt-4 scroll-mt-24"
    >
      {children}
    </h2>
  );
}

function WeightRegainPage() {
  return (
    <>
      <PageSection className="pt-6 pb-4 text-center">
        <h1 className="text-4xl md:text-5xl font-serif text-brand-mid">
          Managing Weight Regain After Bariatric Surgery
        </h1>
        <p className="mt-3 text-lg text-muted-foreground italic">
          Why the weight comes back — and what actually works to take it off again
        </p>
      </PageSection>

      <PageSection maxWidth="4xl" className="pb-10">
        <nav aria-label="On this page" className="mb-8 rounded-2xl border border-border bg-card p-5">
          <h2 className="text-lg font-serif text-brand-mid">On this page</h2>
          <ul className="mt-2 grid gap-1 sm:grid-cols-2 text-sm">
            <li><a className="text-brand-mid underline" href="#how-common">How common is weight regain?</a></li>
            <li><a className="text-brand-mid underline" href="#biology">The biology behind regain</a></li>
            <li><a className="text-brand-mid underline" href="#evaluation">How we evaluate regain</a></li>
            <li><a className="text-brand-mid underline" href="#glp1">GLP-1 medications after surgery</a></li>
            <li><a className="text-brand-mid underline" href="#revision">Bariatric revision options</a></li>
            <li><a className="text-brand-mid underline" href="#sleeve-to-bypass">Sleeve to bypass conversion</a></li>
            <li><a className="text-brand-mid underline" href="#long-term">Long-term metabolic health</a></li>
            <li><a className="text-brand-mid underline" href="#faq">Frequently asked questions</a></li>
          </ul>
        </nav>

        <article className="leading-[1.6] text-base md:text-lg space-y-5 [&_p]:mb-0">
          <p>
            If you had{" "}
            <Link className="text-brand-mid underline" to="/services/sleeve">gastric sleeve surgery</Link>{" "}
            or a gastric bypass and the scale has started
            climbing again, you are not an outlier and you did not fail. Weight regain

            after bariatric surgery is one of the most common reasons patients come back
            to see us, and in 2026 there are more effective ways to reverse it than there
            have ever been. This guide explains why regain happens, how we sort out the
            cause, and what your realistic options are — from medication to revisional
            surgery. If you have not yet had an operation, start with our{" "}
            <Link to="/resources/qualifications" className="text-brand-mid underline">
              bariatric surgery qualifications
            </Link>{" "}
            guide; this page is for patients already past that step.
          </p>

          <H2 id="how-common">How common is weight regain after gastric sleeve or bypass?</H2>
          <p>
            Almost everyone regains some weight after hitting their lowest point, usually
            starting somewhere between eighteen months and three years after surgery. A
            rebound of roughly 5 to 10 percent of body weight is normal and does not
            signal a problem. What matters clinically is the size and the trajectory of
            the regain, and whether the health conditions your surgery fixed are coming
            back.
          </p>
          <p>
            We generally want to take a closer look when you have regained more than about
            a quarter of the weight you originally lost, when your BMI has climbed back
            above 35, or when diabetes, high blood pressure, sleep apnea, or reflux have
            returned. Those are the thresholds where treating the regain changes your
            long-term health, not just your clothing size.
          </p>

          <H2 id="biology">The biology behind weight regain</H2>
          <p>
            Regain after bariatric surgery is driven by physiology, not willpower. Large
            weight loss triggers a coordinated defense response in the body, and that
            response gets stronger the more weight you lose.
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Metabolic adaptation.</strong> A smaller body burns fewer calories
              at rest, and it burns fewer than body size alone would predict. Your
              maintenance calorie budget after surgery is genuinely lower than it was
              before.
            </li>
            <li>
              <strong>Hunger hormones return.</strong> Sleeve gastrectomy removes most of
              the ghrelin-producing part of the stomach, which is a major reason appetite
              drops so sharply at first. Over the years the remaining tissue and the small
              intestine partially compensate, and hunger comes back.
            </li>
            <li>
              <strong>Fullness signaling fades.</strong> GLP-1 and PYY, the gut hormones
              that tell your brain you have eaten enough, surge after surgery and then
              settle. As they settle, portions creep up.
            </li>
            <li>
              <strong>Anatomic change.</strong> A sleeve can dilate over time, and after a
              bypass the pouch or the connection to the intestine can widen, so more food
              is tolerated at a sitting.
            </li>
            <li>
              <strong>Behavior and life circumstances.</strong> Grazing, liquid calories,
              alcohol, sleep loss, medications that promote weight gain, and stretches of
              high stress all layer on top of the biology.
            </li>
          </ul>
          <p>
            None of these are moral failings. They are predictable, measurable, and — this
            is the important part — treatable.
          </p>

          <H2 id="evaluation">How we evaluate weight regain</H2>
          <p>
            Effective treatment starts with finding out which of the causes above is
            actually driving your regain, because the answer determines whether you need
            a medication, a behavioral plan, or an operation. A typical work-up includes a
            detailed diet and eating-pattern history, a review of medications that promote
            weight gain, labs to check for nutritional and metabolic problems, and imaging
            or an upper endoscopy to look at your current anatomy.
          </p>
          <p>
            That last step matters more than most patients expect. An upper GI contrast
            study will show whether a sleeve has dilated, whether a bypass pouch has
            enlarged, or whether there is a hiatal hernia or a fistula contributing to
            your symptoms. Without that picture, choosing between medication and surgery
            is guesswork.
          </p>

          <H2 id="glp1">GLP-1 medications after bariatric surgery</H2>
          <p>
            For a large share of patients with regain, the right first step is medication
            rather than a second operation. Drugs such as semaglutide and tirzepatide
            directly replace the fullness signaling that faded after your surgery, which
            is why they work so well in post-surgical patients. In practice we commonly
            see clinically meaningful loss of regained weight, and patients avoid the
            recovery, cost, and risk of another procedure.
          </p>
          <p>
            The honest caveats: these medications are an ongoing treatment rather than a
            cure, weight tends to return if they are stopped, and coverage in Virginia
            varies widely by plan. Our{" "}
            <Link className="text-brand-mid underline" to="/medical-weight-loss/qualifications">
              medication qualification guide
            </Link>{" "}
            walks through the BMI criteria, and our{" "}
            <Link className="text-brand-mid underline" to="/medical-weight-loss/insurance">
              Virginia insurance hub
            </Link>{" "}
            explains what each major payor requires.
          </p>

          <H2 id="revision">Bariatric revision options</H2>
          <p>
            When anatomy is the problem, or when medication is not enough, revisional
            surgery is on the table. The realistic options are:
          </p>
          <ul className="list-disc pl-6 space-y-2">
            <li>
              <strong>Sleeve to Roux-en-Y gastric bypass.</strong> The most common
              revision we perform, and the best choice when reflux accompanies the regain.
            </li>
            <li>
              <strong>Sleeve to duodenal switch or SADI.</strong> A more powerful
              malabsorptive option for patients with a high BMI and substantial regain,
              with a correspondingly greater need for lifelong vitamin follow-up.
            </li>
            <li>
              <strong>Bypass revision.</strong> Tightening an enlarged pouch or a dilated
              connection to the intestine, for the subset of bypass patients whose imaging
              shows that specific problem.
            </li>
            <li>
              <strong>Band removal and conversion.</strong> For patients with a failed
              adjustable gastric band, removing the band and converting to a sleeve or
              bypass is usually the durable answer. See our{" "}
              <Link className="text-brand-mid underline" to="/services/revisions">
                revisional surgery page
              </Link>
              .
            </li>
          </ul>
          <p>
            Revisional operations are technically harder than first-time surgery. Scar
            tissue and altered anatomy leave less margin for error, so volume and
            experience matter. Dr. Fitzer has performed more than 6,000 operations over
            his career, including a large volume of revisions, and performs the great
            majority of them laparoscopically, which means small incisions and a faster
            recovery.
          </p>

          <H2 id="sleeve-to-bypass">Converting a sleeve to a bypass</H2>
          <p>
            The sleeve-to-bypass conversion deserves its own note because it is the
            revision patients ask about most. It does two things at once: it restores
            restriction with a small pouch, and it adds the hormonal and malabsorptive
            effect of the bypass. It is also the definitive fix for the severe reflux that
            some sleeve patients develop, which is a common reason to revise even when
            weight is stable.
          </p>
          <p>
            Expect meaningful additional weight loss, an operation that is more involved
            than a primary bypass, and a lifelong commitment to vitamin supplementation
            and follow-up labs. Our{" "}
            <Link className="text-brand-mid underline" to="/services/bypass-vs-sleeve">
              bypass versus sleeve comparison
            </Link>{" "}
            covers the trade-offs between the two anatomies in detail.
          </p>

          <H2 id="long-term">Long-term metabolic health, not just the scale</H2>
          <p>
            The reason to treat regain is not vanity. Regained weight brings back the
            metabolic disease that surgery resolved: insulin resistance and type 2
            diabetes, hypertension, sleep apnea, fatty liver, and joint pain. Reversing
            regain early protects the remission you earned.
          </p>
          <p>
            The patients who hold their results longest are the ones who stay connected to
            a program — regular follow-up visits, annual labs, protein-forward eating,
            resistance training to preserve muscle, and a plan that adjusts when life
            changes. If you have been away from bariatric care for years, coming back is
            the single most useful thing you can do.
          </p>

          <H2 id="faq">Frequently asked questions</H2>
          <dl className="space-y-5">
            {FAQS.map((f) => (
              <div key={f.q}>
                <dt className="font-medium text-brand-mid">{f.q}</dt>
                <dd className="mt-1">{f.a}</dd>
              </div>
            ))}
          </dl>

          <H2>Talk with us about your regain</H2>
          <p>
            Weight regain is far easier to reverse at fifteen pounds than at sixty. If the
            trend has been going the wrong way, schedule an evaluation — whether you had
            your original surgery with us or somewhere else — and we will tell you plainly
            whether medication, a revision, or a tune-up of your follow-up plan is the
            right next step.
          </p>
        </article>

        <div className="mt-10 flex flex-wrap justify-center gap-3">
          <Link
            to="/contact"
            className="rounded-full bg-primary px-7 py-3 text-sm font-medium text-primary-foreground hover:opacity-90"
          >
            Request an Evaluation
          </Link>
          <Link
            to="/resources"
            className="rounded-full bg-brand-logo px-7 py-3 text-sm font-medium text-brand-foreground hover:opacity-90"
          >
            Back to Resources
          </Link>
        </div>

        <section className="mt-12 border-t pt-6">
          <h2 className="text-xl font-serif text-brand-mid mb-3">References</h2>
          <ol className="list-decimal pl-6 space-y-2 text-sm text-muted-foreground leading-snug">
            <li>
              King WC, et al. Comparison of the performance of common measures of weight
              regain after bariatric surgery. <em>JAMA.</em> 2018;320:1560-1569.
              doi:10.1001/jama.2018.14433
            </li>
            <li>
              Fehervari M, et al. Weight loss and remission of diabetes after sleeve
              gastrectomy conversion to Roux-en-Y gastric bypass.{" "}
              <em>Surg Obes Relat Dis.</em> 2023;19:384-395.
              doi:10.1016/j.soard.2022.11.004
            </li>
            <li>
              Jastreboff AM, et al. Tirzepatide once weekly for the treatment of obesity
              (SURMOUNT-1). <em>N Engl J Med.</em> 2022;387:205-216.
              doi:10.1056/NEJMoa2206038
            </li>
          </ol>
        </section>
      </PageSection>
    </>
  );
}
